import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Stack, Typography } from '@lyearn/ui';
import {
  CustomFrequencyEnum,
  DayOfWeekEnum,
  ScheduleInfo,
  ScheduleInfoInput,
} from '@/types/schema';

import { DEFAULT_WEEK_OF_MONTH } from '../../conts';
import { FrequencyComponentProps } from '../../types';
import { getDayOfMonthAndMonthOfYear } from '../../utils';
import FrequencyInput from '../InputComponents/Frequency';
import CustomDay from './components/CustomDay';
import CustomMonth from './components/CustomMonth';
import CustomWeek from './components/CustomWeek';
import CustomYear from './components/CustomYear';

export function getCustomFrequencyInitialValue(value: ScheduleInfoInput): ScheduleInfoInput {
  return {
    ...value,
    custom: {
      interval: 1,
      frequency: CustomFrequencyEnum.Day,
      daysOfWeek: [DayOfWeekEnum.Monday],
    },
  };
}

const FrequencyComponents = {
  [CustomFrequencyEnum.Minute]: {
    Component: () => null,
    getInitialValue: (value: ScheduleInfo): ScheduleInfo => {
      return { ...value, custom: { interval: 1, frequency: CustomFrequencyEnum.Minute } };
    },
  },
  [CustomFrequencyEnum.Day]: {
    Component: CustomDay,
    getInitialValue: (value: ScheduleInfo): ScheduleInfo => {
      const { frequency, timezone, time, endScheduleInfo, custom } = value;
      return { frequency, timezone, time, endScheduleInfo, custom };
    },
  },
  [CustomFrequencyEnum.Week]: {
    Component: CustomWeek,
    getInitialValue: (value: ScheduleInfo): ScheduleInfo => {
      const { interval, daysOfWeek } = value.custom || {};
      return {
        ...value,
        custom: {
          interval: interval ?? 1,
          frequency: CustomFrequencyEnum.Week,
          daysOfWeek: daysOfWeek ?? [DayOfWeekEnum.Monday],
        },
      };
    },
  },
  [CustomFrequencyEnum.Month]: {
    Component: CustomMonth,
    getInitialValue: (value: ScheduleInfo): ScheduleInfo => {
      const { interval, daysOfWeek, weeksOfMonth, weekOfMonth } = value.custom || {};
      return {
        ...value,
        custom: {
          interval: interval ?? 1,
          frequency: CustomFrequencyEnum.Month,
          daysOfWeek: daysOfWeek ?? [DayOfWeekEnum.Monday],
          weeksOfMonth: weeksOfMonth ?? (weekOfMonth ? [weekOfMonth] : [DEFAULT_WEEK_OF_MONTH]),
        },
      };
    },
  },
  [CustomFrequencyEnum.Year]: {
    Component: CustomYear,
    getInitialValue: (value: ScheduleInfo): ScheduleInfo => {
      const { interval, dayOfMonth, monthOfYear } = value.custom || {};
      const update_field = getDayOfMonthAndMonthOfYear(value.startDate);

      return {
        ...value,
        custom: {
          interval: interval ?? 1,
          frequency: CustomFrequencyEnum.Year,
          dayOfMonth: dayOfMonth ?? update_field.dayOfMonth,
          monthOfYear: monthOfYear ?? update_field.MonthOfYear,
        },
      };
    },
  },
};

const Custom = ({
  value,
  onChange: onChangeProp,
  hideTimeSelection,
  disabled,
  allowedFrequencies,
  allowedCustomFrequencies,
  errorConfig,
  size,
  overrides,
}: FrequencyComponentProps) => {
  const { t } = useTranslation();

  const onChange = React.useCallback(
    (updatedValues: Partial<ScheduleInfoInput>) => {
      const { getInitialValue } = FrequencyComponents[CustomFrequencyEnum.Day];
      onChangeProp(getInitialValue({ ...value, ...updatedValues }));
    },
    [onChangeProp, value],
  );

  const onChangeCustom = React.useCallback(
    (updatedValue: Partial<ScheduleInfoInput>) => {
      const customValue = value.custom!;
      const updatedCustomValue = updatedValue.custom;

      if (updatedCustomValue?.frequency && customValue.frequency !== updatedCustomValue.frequency) {
        const { getInitialValue } = FrequencyComponents[updatedCustomValue.frequency];
        onChangeProp(getInitialValue({ ...value, ...updatedValue }));
      } else {
        onChangeProp({ ...value, ...updatedValue });
      }
    },
    [onChangeProp, value],
  );

  const { Component } = FrequencyComponents[value.custom?.frequency || CustomFrequencyEnum.Day];
  return (
    <Stack direction="col">
      <FrequencyInput
        allowedFrequencies={allowedFrequencies}
        disabled={disabled}
        size={size}
        value={value}
        onChange={onChange}
      />
      <Typography className="mt-16 mb-8 ms-6" color="text-text-primary" variant="body-short-02">
        {t('RepeatsEvery')}
      </Typography>
      <Component
        allowedCustomFrequencies={allowedCustomFrequencies}
        disabled={disabled}
        errorConfig={errorConfig}
        hideTimeSelection={hideTimeSelection}
        overrides={overrides}
        size={size}
        value={value}
        onChange={onChangeCustom}
      />
    </Stack>
  );
};

export default Custom;
